import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { ArrowLeft, Save, Loader2, Trash2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useAdminUsers } from '@/hooks/useAdminUsers'
import type { UpdateUserFormData } from '@/types/admin'

// Form validation schema
const userEditFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  display_name: z.string().min(2, 'Display name must be at least 2 characters'),
  username: z.string().optional(),
  bio: z.string().optional(),
  avatar_url: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  website_url: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  is_banned: z.boolean().optional(),
  // Social links as individual fields for easier validation
  social_facebook: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  social_twitter: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  social_instagram: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  social_youtube: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  social_tiktok: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  social_linkedin: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  social_github: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
})

type UserEditFormValues = z.infer<typeof userEditFormSchema>

export default function UserEdit() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { fetchUser, updateUser, deleteUser } = useAdminUsers()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [emailChanged, setEmailChanged] = useState(false)

  const form = useForm<UserEditFormValues>({
    resolver: zodResolver(userEditFormSchema),
    defaultValues: {
      email: '',
      display_name: '',
      username: '',
      bio: '',
      avatar_url: '',
      website_url: '',
      is_banned: false,
      social_facebook: '',
      social_twitter: '',
      social_instagram: '',
      social_youtube: '',
      social_tiktok: '',
      social_linkedin: '',
      social_github: '',
    },
  })

  // Load user data on mount
  useEffect(() => {
    if (!userId) {
      navigate('/admin')
      return
    }

    const loadUser = async () => {
      const user = await fetchUser(userId)
      if (user) {
        // Extract social links from the social_links object
        const socialLinks = user.social_links || {}

        form.reset({
          email: user.email || '',
          display_name: user.display_name || '',
          username: user.username || '',
          bio: user.bio || '',
          avatar_url: user.avatar_url || '',
          website_url: user.website_url || '',
          is_banned: user.is_banned || false,
          social_facebook: socialLinks.facebook || '',
          social_twitter: socialLinks.twitter || '',
          social_instagram: socialLinks.instagram || '',
          social_youtube: socialLinks.youtube || '',
          social_tiktok: socialLinks.tiktok || '',
          social_linkedin: socialLinks.linkedin || '',
          social_github: socialLinks.github || '',
        })
      }
      setLoading(false)
    }

    loadUser()
  }, [userId]) // Only userId should trigger reload - form.reset has stable reference per React Hook Form docs

  // Watch for email changes
  const watchedEmail = form.watch('email')
  useEffect(() => {
    const originalEmail = form.formState.defaultValues?.email
    setEmailChanged(!!originalEmail && watchedEmail !== originalEmail)
  }, [watchedEmail, form.formState.defaultValues?.email])

  const onSubmit = async (values: UserEditFormValues) => {
    if (!userId) return

    try {
      setIsSubmitting(true)

      // Build social_links object from individual fields
      const socialLinks: Record<string, string> = {}
      if (values.social_facebook) socialLinks.facebook = values.social_facebook
      if (values.social_twitter) socialLinks.twitter = values.social_twitter
      if (values.social_instagram) socialLinks.instagram = values.social_instagram
      if (values.social_youtube) socialLinks.youtube = values.social_youtube
      if (values.social_tiktok) socialLinks.tiktok = values.social_tiktok
      if (values.social_linkedin) socialLinks.linkedin = values.social_linkedin
      if (values.social_github) socialLinks.github = values.social_github

      const userData: UpdateUserFormData = {
        email: values.email,
        display_name: values.display_name,
        username: values.username || undefined,
        bio: values.bio || undefined,
        avatar_url: values.avatar_url || undefined,
        website_url: values.website_url || undefined,
        is_banned: values.is_banned,
        social_links: Object.keys(socialLinks).length > 0 ? socialLinks : undefined,
      }

      const result = await updateUser(userId, userData)

      if (result) {
        // Redirect to admin page with users tab active
        navigate('/admin?tab=users')
      }
    } catch (error) {
      console.error('Error updating user:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!userId) return

    setIsDeleting(true)
    const success = await deleteUser(userId)
    setIsDeleting(false)

    if (success) {
      navigate('/admin?tab=users')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Edit User</h1>
            <p className="text-muted-foreground">
              Update user information and manage account status
            </p>
          </div>
        </div>

        {/* Email Change Warning */}
        {emailChanged && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Changing the email address will send a verification email to the new address.
              The user will need to verify before the email change takes effect.
            </AlertDescription>
          </Alert>
        )}

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>
              Update the user's details below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Email Field */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="user@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Changing email will trigger email verification
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Display Name Field */}
                <FormField
                  control={form.control}
                  name="display_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Username Field */}
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="johndoe"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9_-]/g, '')
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Bio Field */}
                <FormField
                  control={form.control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bio</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Tell us about this user..."
                          className="resize-none"
                          rows={4}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Avatar URL Field */}
                <FormField
                  control={form.control}
                  name="avatar_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Avatar URL</FormLabel>
                      <FormControl>
                        <Input
                          type="url"
                          placeholder="https://example.com/avatar.jpg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Website URL Field */}
                <FormField
                  control={form.control}
                  name="website_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL</FormLabel>
                      <FormControl>
                        <Input
                          type="url"
                          placeholder="https://example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Social Links Section */}
                <div className="pt-4 border-t">
                  <h3 className="text-lg font-semibold mb-4">Social Media Links</h3>
                  <div className="space-y-4">
                    {/* Facebook */}
                    <FormField
                      control={form.control}
                      name="social_facebook"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Facebook</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://facebook.com/username"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Twitter */}
                    <FormField
                      control={form.control}
                      name="social_twitter"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Twitter / X</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://twitter.com/username"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Instagram */}
                    <FormField
                      control={form.control}
                      name="social_instagram"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Instagram</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://instagram.com/username"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* YouTube */}
                    <FormField
                      control={form.control}
                      name="social_youtube"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>YouTube</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://youtube.com/@username"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* TikTok */}
                    <FormField
                      control={form.control}
                      name="social_tiktok"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>TikTok</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://tiktok.com/@username"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* LinkedIn */}
                    <FormField
                      control={form.control}
                      name="social_linkedin"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>LinkedIn</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://linkedin.com/in/username"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* GitHub */}
                    <FormField
                      control={form.control}
                      name="social_github"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>GitHub</FormLabel>
                          <FormControl>
                            <Input
                              type="url"
                              placeholder="https://github.com/username"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Account Status */}
                <FormField
                  control={form.control}
                  name="is_banned"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Account Status
                        </FormLabel>
                        <FormDescription>
                          Ban this user from accessing the platform
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Form Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/admin?tab=users')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
            <CardDescription>
              Irreversible actions that will permanently affect this user
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Delete User
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the user's
                    account and remove all associated data from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Deleting...
                      </>
                    ) : (
                      'Delete User'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
